import MatchEnum from "~src/enums/badminton/MatchEnum";
import type {
  IMatch,
  IMatchLive,
  IScores,
  IStatistics,
  ITeam,
} from "~src/interfaces/badminton";
import StatsEnum from "~src/enums/badminton/StatsEnum";
import MatchBadmintonEnum from "~src/enums/badminton/MatchEnum";

const calculateDynamics = (score: any, index: any) => {
  let homeTotal = 0;
  let awayTotal = 0;

  // Iterate over the keys of the score object
  // eslint-disable-next-line no-restricted-syntax
  for (const key in score) {
    if (index >= 0) {
      if (key.startsWith(`p${index + 1}`)) {
        homeTotal += score[key][0]; // Add home score[3] for this set
        awayTotal += score[key][1]; // Add away score for this set
      }
    } else if (key.startsWith(`p`)) {
      homeTotal += score[key][0]; // Add home score[3] for this set
      awayTotal += score[key][1]; // Add away score for this set
    }
  }

  return { homeTotal, awayTotal };
};

const calculateTeamData = (stats: any, index: any) => {
  try {
    const statsOfSet = stats.find((stat: any) => stat[0] === index);

    const homeData = {
      total_points: 0,
      points_won: 0,
      max_points_in_a_row: 0,
      match_points: 0,
      comeback_to_win: 0,
      biggest_lead: 0,
      service_errors: 0,
      service_points_rate: 0,
      service_points_total: 0,
      service_points_success: 0,
      receiver_points_success: 0,
      receiver_points_total: 0,
      receiver_points_rate: 0,
    };

    const awayData = {
      total_points: 0,
      points_won: 0,
      max_points_in_a_row: 0,
      match_points: 0,
      comeback_to_win: 0,
      biggest_lead: 0,
      service_errors: 0,
      service_points_rate: 0,
      service_points_total: 0,
      service_points_success: 0,
      receiver_points_success: 0,
      receiver_points_total: 0,
      receiver_points_rate: 0,
    };
    if (!statsOfSet) {
      return { homeData, awayData };
    }
    statsOfSet[1].forEach((stat: any) => {
      const [statType, homeStat, awayStat] = stat;
      if (statType === 1) {
        homeData.points_won = homeStat;
        awayData.points_won = awayStat;
      }
      if (statType === 2) {
        homeData.max_points_in_a_row = homeStat;
        awayData.max_points_in_a_row = awayStat;
      }
      if (statType === 3) {
        homeData.match_points = homeStat;
        awayData.match_points = awayStat;
      }
      if (statType === 4) {
        homeData.comeback_to_win = homeStat;
        awayData.comeback_to_win = awayStat;
      }
      if (statType === 5) {
        homeData.biggest_lead = homeStat;
        awayData.biggest_lead = awayStat;
      }
      if (statType === 6) {
        homeData.service_errors = homeStat;
        awayData.service_errors = awayStat;
      }
      if (statType === 7) {
        homeData.service_points_success = homeStat;
        awayData.service_points_success = awayStat;
      }
      if (statType === 8) {
        homeData.service_points_total = homeStat;
        awayData.service_points_total = awayStat;
      }
      if (statType === 9) {
        homeData.service_points_rate = homeStat;
        awayData.service_points_rate = awayStat;
      }
      if (statType === 10) {
        homeData.receiver_points_success = homeStat;
        awayData.receiver_points_success = awayStat;
      }
      if (statType === 11) {
        homeData.receiver_points_total = homeStat;
        awayData.receiver_points_total = awayStat;
      }
      if (statType === 12) {
        homeData.receiver_points_rate = homeStat;
        awayData.receiver_points_rate = awayStat;
      }
    });
    homeData.total_points = homeData.points_won;
    awayData.total_points = awayData.points_won;
    return { homeData, awayData };
  } catch (error) {
    throw error;
  }
};

export const mapMatchData = (matchData: any) => {
  try {
    let matches = matchData
      .filter((m: any) => {
        return [51, 52, 53, 54, 55, 100].includes(m.status_id);
      })
      .sort((a: any, b: any) => {
        return a.status_id - b.status_id > 0 ? 1 : -1;
      });

    const mapStatus = {
      "0": 0,
      "1": 51,
      "2": 52,
      "3": 53,
      "4": 54,
      "5": 55,
    };
    matches = matches[matches.length - 1];

    const { stats } = matches;
    const t = stats.map((stat: any) => {
      let [index, detailStat]: [keyof typeof mapStatus, any] = stat;
      const { homeData, awayData } = calculateTeamData(stats, index);
      return {
        status_id: mapStatus[index],
        homeData,
        awayData,
      };
    });

    return t;
  } catch (error) {
    throw error;
  }
};

const calculatePercentage = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};

const chooseValue = (
  statistics: IStatistics | any,
  key: string,
  index: number
) => {
  let team = index === 1 ? "awayData" : "homeData";
  let teamData = index === 1 ? "homeData" : "awayData";
  let data;
  if (key === "total_points") {
    data = statistics[teamData].total_points;
  }

  if (key === "points_won") {
    data = statistics[teamData].points_won;
  }

  if (key === "max_points_in_a_row") {
    data = statistics[teamData].max_points_in_a_row;
  }

  if (key === "match_points") {
    data = statistics[teamData].match_points;
  }

  if (key === "comeback_to_win") {
    data = statistics[teamData].comeback_to_win;
  }

  if (key === "biggest_lead") {
    data = statistics[teamData].biggest_lead;
  }

  if (key === "service_errors") {
    data = statistics[teamData].service_errors;
  }

  if (key === "service_points_rate") {
    data = `${(statistics[teamData].service_points_rate * 100).toFixed(1)}%`;
  }

  if (key === "service_points_total") {
    data = statistics[teamData].service_points_total;
  }

  if (key === "service_points_success") {
    data = statistics[teamData].service_points_success;
  }

  if (key === "max_service_points_success") {
    data = `${statistics[teamData].service_points_success}/${statistics[teamData].service_points_total}`;
  }

  if (key === "receiver_points_success") {
    data = statistics[teamData].receiver_points_success;
  }

  if (key === "receiver_points_total") {
    data = statistics[teamData].receiver_points_total;
  }

  if (key === "receiver_points_rate") {
    data = `${(statistics[teamData].receiver_points_rate * 100).toFixed(1)}%`;
  }

  if (key === "max_receiver_points_success") {
    data = `${statistics[teamData].receiver_points_success}/${statistics[team].service_points_total}`;
  }

  return data;
};

export const getProcessedStats = (
  match: IMatch,
  statistics: IStatistics,
  isDefault: boolean
) => {
  const techKind: Record<string, string> = StatsEnum.techKind;

  return Object.keys(techKind).map((forwardKey) => {
    let key = forwardKey;

    let showAwayPre = calculatePercentage(
      Number(chooseValue(statistics, key, 2)),
      Number(chooseValue(statistics, key, 1)) +
        Number(chooseValue(statistics, key, 2))
    );
    let showHomePre = calculatePercentage(
      Number(chooseValue(statistics, key, 1)),
      Number(chooseValue(statistics, key, 1)) +
        Number(chooseValue(statistics, key, 2))
    );
    let winHome = Number(chooseValue(statistics, key, 1));
    let winAway = Number(chooseValue(statistics, key, 2));

    let home = chooseValue(statistics, forwardKey, 1);
    let away = chooseValue(statistics, forwardKey, 2);

    if (key === "service_points_rate") {
      showAwayPre = calculatePercentage(
        Number(chooseValue(statistics, "service_points_success", 2)),
        Number(chooseValue(statistics, "service_points_success", 1)) +
          Number(chooseValue(statistics, "service_points_success", 2))
      );

      showHomePre = calculatePercentage(
        Number(chooseValue(statistics, "service_points_success", 1)),
        Number(chooseValue(statistics, "service_points_success", 1)) +
          Number(chooseValue(statistics, "service_points_success", 2))
      );

      winHome = Number(chooseValue(statistics, "service_points_success", 1));
      winAway = Number(chooseValue(statistics, "service_points_success", 2));
    }
    if (key === "receiver_points_rate") {
      showAwayPre = calculatePercentage(
        Number(chooseValue(statistics, "receiver_points_success", 2)),
        Number(chooseValue(statistics, "receiver_points_success", 1)) +
          Number(chooseValue(statistics, "receiver_points_success", 2))
      );

      showHomePre = calculatePercentage(
        Number(chooseValue(statistics, "receiver_points_success", 1)),
        Number(chooseValue(statistics, "receiver_points_success", 1)) +
          Number(chooseValue(statistics, "receiver_points_success", 2))
      );

      winHome = Number(chooseValue(statistics, "receiver_points_success", 1));
      winAway = Number(chooseValue(statistics, "receiver_points_success", 2));
    }

    if (key === "service_points_success") {
      showAwayPre = calculatePercentage(
        Number(chooseValue(statistics, "service_points_success", 2)),
        Number(chooseValue(statistics, "service_points_success", 1)) +
          Number(chooseValue(statistics, "service_points_success", 2))
      );

      showHomePre = calculatePercentage(
        Number(chooseValue(statistics, "service_points_success", 1)),
        Number(chooseValue(statistics, "service_points_success", 1)) +
          Number(chooseValue(statistics, "service_points_success", 2))
      );

      winHome = Number(chooseValue(statistics, "service_points_success", 1));
      winAway = Number(chooseValue(statistics, "service_points_success", 2));
      home = chooseValue(statistics, "max_service_points_success", 1);
      away = chooseValue(statistics, "max_service_points_success", 2);
    }

    if (key === "receiver_points_success") {
      showAwayPre = calculatePercentage(
        Number(chooseValue(statistics, "receiver_points_success", 2)),
        Number(chooseValue(statistics, "receiver_points_success", 1)) +
          Number(chooseValue(statistics, "receiver_points_success", 2))
      );

      showHomePre = calculatePercentage(
        Number(chooseValue(statistics, "receiver_points_success", 1)),
        Number(chooseValue(statistics, "receiver_points_success", 1)) +
          Number(chooseValue(statistics, "receiver_points_success", 2))
      );

      winHome = Number(chooseValue(statistics, "receiver_points_success", 1));
      winAway = Number(chooseValue(statistics, "receiver_points_success", 2));
      home = chooseValue(statistics, "max_receiver_points_success", 1);
      away = chooseValue(statistics, "max_receiver_points_success", 2);
    }

    return {
      key,
      type: techKind[forwardKey],
      home,
      away,
      showAwayPre,
      showHomePre,
      winHome: winHome > winAway ? "as-statistics__home-win" : "",
      winAway: winAway > winHome ? "as-statistics__away-win" : "",
    };
  });
};

export const initializeData = () => ({
  score: { home: 0, away: 0 },
  homeData: {
    total_points: 0,
    points_won: 0,
    max_points_in_a_row: 0,
    match_points: 0,
    comeback_to_win: 0,
    biggest_lead: 0,
    service_errors: 0,
    service_points_rate: 0,
    service_points_total: 0,
    service_points_success: 0,
    receiver_points_success: 0,
    receiver_points_total: 0,
    receiver_points_rate: 0,
  },
  awayData: {
    total_points: 0,
    points_won: 0,
    max_points_in_a_row: 0,
    match_points: 0,
    comeback_to_win: 0,
    biggest_lead: 0,
    service_errors: 0,
    service_points_rate: 0,
    service_points_total: 0,
    service_points_success: 0,
    receiver_points_success: 0,
    receiver_points_total: 0,
    receiver_points_rate: 0,
  },
  id: "",
  current_type: 0,
  status_id: 0,
  type: 100,
});

export const getScoresTeam = (scores: Record<string, [number, number]>) => {
  const _home: number[] = [];
  const _away: number[] = [];

  Object.values(scores).forEach(([homeScore, awayScore]) => {
    _home.push(homeScore);
    _away.push(awayScore);
  });

  return {
    home: _home,
    away: _away,
  };
};

const getCurrentDateTimestamp = () => {
  const currentDateTimestamp = Math.floor(Date.now() / 1000);

  return currentDateTimestamp;
};

const timestampToMinutesSeconds = (timestamp: number) => {
  const minutes = Math.floor(timestamp / 60);
  const seconds = timestamp % 60;

  return { minutes, seconds };
};

export const getTimeDetailLive = (
  statusId: number,
  startTimestamp: number | null = null
) => {
  const statusText = MatchBadmintonEnum.MATCH_STATUS_TEXT as {
    [key: string]: any;
  };

  const statusTxt = statusText[statusId] || "";
  let timePlayerProcess = 0;

  if (!startTimestamp) {
    return {
      text: statusTxt,
      process: timePlayerProcess,
    };
  }

  const currentDate: number = getCurrentDateTimestamp();
  const { minutes } = timestampToMinutesSeconds(12 * 60 - startTimestamp);
  const minutesApp: number = minutes + 1;

  // % thời gian đá
  // const timeProcess = {
  //   2: minutesApp > 45 ? 50 : ((minutesApp / 120) * 100),
  //   4: minutesApp + 45 > 100 ? 90 : (((45 + minutesApp) / 120) * 100),
  //   5:
  //     minutesApp + 90 > 105
  //       ? (105 / 120) * 100
  //       : (((90 + minutesApp) / 120) * 100),
  //   6:
  //     minutesApp + 105 > 120 ? 100 : (((105 + minutesApp) / 120) * 100),
  // } as {[key: number]: any;};

  const statusTimeTexts = {
    2: minutesApp > 12 ? "12+'" : minutesApp + "'",
    4: minutesApp > 12 ? "12+'" : minutesApp + "'",
    6: minutesApp > 12 ? "12+'" : minutesApp + "'",
    8: minutesApp > 12 ? "12+'" : minutesApp + "'",
  } as { [key: number]: any };

  const timeText = statusTimeTexts[statusId]
    ? `: ${statusTimeTexts[statusId]}`
    : "";

  // timePlayerProcess = timeProcess[statusId] ? timeProcess[statusId] : 0;

  return {
    text: `${statusTxt}${timeText}`,
    process: timePlayerProcess,
  };
};

export const determineMatchOutcome = (matchData: any, homeTeamId: string) => {
  const home_team_id = matchData.home_team_id;
  const away_team_id = matchData.away_team_id;
  const home_scores = matchData.scores
    ? getScoresTeam(matchData.scores).home
    : [];
  const away_scores = matchData.scores
    ? getScoresTeam(matchData.scores).away
    : [];

  const totalHomeGoals = home_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  const totalAwayGoals = away_scores.reduce(
    (partialSum: number, a: number) => partialSum + a,
    0
  );

  let homeWinTotal = 0;
  let awayWinTotal = 0;
  home_scores.forEach((score: number, index: number) => {
    if (score > away_scores[index]) {
      homeWinTotal++;
    } else {
      awayWinTotal++;
    }
  });

  const isActualHomeTeam = home_team_id === homeTeamId;
  // Xác định trạng thái trận đấu dựa trên tổng số bàn thắng
  if (homeWinTotal > awayWinTotal) {
    return isActualHomeTeam ? "win" : "lose"; // Kiểm tra đội nhà có phải là đội mình quan tâm không
  } else if (homeWinTotal === awayWinTotal) {
    return "draw"; // Hòa
  } else {
    return isActualHomeTeam ? "lose" : "win"; // Đảo ngược kết quả nếu đội mình quan tâm không phải là đội nhà
  }
};

export const parseMatchH2hData = (item: any, homeTeamId: string) => {
  const listStatusPlaying = [51, 52, 53, 54, 55];

  const statusEnd = 100;
  const status_id = item.status_id;
  const Kick_off_time = item.match_time;
  const home_team_id = item.home_team_id;
  const away_team_id = item.away_team_id;
  const home_team = item.home_team;
  const away_team = item.away_team;
  const home_scores = item.scores ? getScoresTeam(item.scores).home : [];
  const away_scores = item.scores ? getScoresTeam(item.scores).away : [];
  let matchTimePlayer = null;
  if (listStatusPlaying.includes(status_id)) {
    matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
  } else if (status_id === statusEnd) {
    const status = determineMatchOutcome(item, homeTeamId);
    let statusTxt = "";
    if (status === "win") {
      statusTxt = "Thắng";
    } else if (status === "draw") {
      statusTxt = "Hòa";
    } else {
      statusTxt = "Thua";
    }

    matchTimePlayer = {
      text: statusTxt,
      status_h2h: status,
    };
  }
  const __data = {
    id: item.id,
    competition_id: item.tournament_id,
    status_id,
    match_time: item.match_time,
    Kick_off_time,
    home_scores,
    away_scores,
    home_team_id,
    away_team_id,
    match_time_player: matchTimePlayer,
    scores: item?.scores ?? {},
    away_team,
    home_team,
  };

  return __data;
};

export const mapTeamToH2H = (data: any) => {
  const teamsMap = new Map<string, any>();
  const tournamentsMap = new Map<string, any>();
  const countriesMap = new Map<string, any>();
  data.teams.forEach((team: any) => {
    teamsMap.set(team.id, team);
  });
  data.tournaments.forEach((tournament: any) => {
    tournamentsMap.set(tournament.id, tournament);
  });

  if (data.countries) {
    data.countries.forEach((country: any) => {
      countriesMap.set(country.id, country);
    });
  }

  const enrichMatches = (matches: any) => {
    return matches.map((match: any) => ({
      ...match,
      home_team: teamsMap.get(match.home_team_id),
      away_team: teamsMap.get(match.away_team_id),
      tournament: tournamentsMap.get(match.tournament_id),
    }));
  };

  data.home = enrichMatches(data.historyHomeMatches);
  data.away = enrichMatches(data.historyAwayMatches);
  data.vs = enrichMatches(data.historyVSMatches);
  data.future.home = enrichMatches(data.future.home);
  data.future.away = enrichMatches(data.future.away);

  data.teams = data.teams.map((team: any) => ({
    ...team,
    country: team.country_id ? countriesMap.get(team.country_id) : undefined,
  }));

  return data;
};

export const getIdsTournaments = (tournaments: any, ground_id: number = 0) => {
  return tournaments
    .filter((item: any) => (ground_id ? item?.ground_id === ground_id : true))
    .map((item: any) => item.id);
};

export const addMatchDetailLive = async (
  matchs: Array<IMatch>,
  detailLive: Array<IMatchLive>,
  teams: ITeam[]
) => {
  const datas = [];
  const listStatusPlaying = MatchEnum.LIST_PLAYING;
  const statusEnd = MatchEnum.ENDED;

  const teamsMap = new Map<string, ITeam>();
  teams.forEach((team: ITeam) => {
    teamsMap.set(team.id, team);
  });

  for (const match of matchs) {
    const matchDetail = detailLive.find((item) => match.id === item.id);
    const status_id = match.status_id;
    const Kick_off_time = match.match_time ?? 0;

    let matchTimePlayer = null;
    let _scores: IScores = {
      ft: ["", ""],
      ot: ["", ""],
      h: ["", ""],
      e: ["", ""],
      p1: ["", ""],
    };

    if (matchDetail) {
      _scores = matchDetail?.score[3] ?? [];
      if (listStatusPlaying.includes(status_id)) {
        matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
      } else if (status_id === statusEnd) {
        const status = determineMatchOutcome(match, match.home_team_id);
        let statusTxt = "";
        if (status === "win") {
          statusTxt = "Thắng";
        } else if (status === "draw") {
          statusTxt = "Hòa";
        } else {
          statusTxt = "Thua";
        }

        matchTimePlayer = {
          text: statusTxt,
          status_h2h: status,
        };
      }
    } else {
      matchTimePlayer = getTimeDetailLive(status_id, Kick_off_time);
    }

    datas.push({
      ...match,
      scores: _scores,
      home_team: teamsMap.get(match.home_team_id) || null,
      away_team: teamsMap.get(match.away_team_id) || null,
      match_time_player: matchTimePlayer,
    });
  }

  return datas;
};
